import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';

/**
 * Shows a bootstrap modal dialog.
 * Set the body of the dialog by adding content to the modal tag: <modal>content here</modal>.
 */
@Component({
  selector: 'modal',
  changeDetection: ChangeDetectionStrategy.Default,
  template: `
  <div class="modal-backdrop fade in" [style.display]="showModal ? 'block' : 'none'"></div>
  <div class="modal" tabindex="-1" role="dialog" style="display: block" [style.display]="showModal ? 'block' : 'none'">
    <div *ngIf="showDialog" class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <!--
          <button type="button"
                  class="close"
                  aria-label="Close"
                  (click)="cancelAction()">
            <span aria-hidden="true">&times;</span>
          </button>
          -->
          <h4 class="modal-title">
            {{title}}
          </h4>
        </div>
        <div class="modal-body">
          <ng-content></ng-content>
        </div>
        <div class="modal-footer">
          <button *ngIf="positiveExtraLabel" type="button"
                  class="btn btn-success btn-md"
                  (click)="positiveExtraAction()">
            {{positiveExtraLabel}}
          </button>
          <button type="button"
                  class="btn btn-success btn-md"
                  (click)="positiveAction()">
            {{positiveLabel}}
          </button>
          <button *ngIf="cancelLabel" type="button"
                  class="btn btn-primary btn-md btn-link"
                  (click)="cancelAction()">
            {{cancelLabel}}
          </button>
        </div>
      </div>
    </div>
  </div>
  `
})
export class Modal implements OnInit {

  @Input('title') title: string;
  @Input('cancel-label') cancelLabel: string = null;
  @Input('positive-label') positiveLabel: string = 'OK';
  @Input('positive-extra-label') positiveExtraLabel: string = null;
  @Input('auto-hide') autoHideOnPositiveAction: boolean = true;
  @Input('show-dialog') showDialog: boolean = true;

  /**
   * Fires an event when the modal is closed. The argument indicated how it was closed.
   * @type {EventEmitter<ModalResult>}
   */
  @Output('closed') closeEmitter: EventEmitter<ModalResult> = new EventEmitter<ModalResult>();
  /**
   * Fires an event when the modal is ready with a pointer to the modal.
   * @type {EventEmitter<Modal>}
   */
  @Output('loaded') loadedEmitter: EventEmitter<Modal> = new EventEmitter<Modal>();

  public showModal: boolean = false;

  constructor(protected cdRef: ChangeDetectorRef) {
    // console.log('showModal = ' + this.showModal);
  }

  ngOnInit() {
    this.loadedEmitter.next(this);
    // console.log('modal inited');
  }

  /**
   * Shows the modal.
   */
  show() {
    this.showModal = true;
    this.cdRef.detectChanges();
  }

  /**
   * Shows the modal.
   */
  hide() {
    this.showModal = false;
    this.cdRef.detectChanges();
  }

  positiveExtraAction() {
    if (this.autoHideOnPositiveAction) {
      this.showModal = false;
    }
    this.closeEmitter.next({
      action: ModalAction.POSITIVE_EXTRA
    });

    this.cdRef.detectChanges();

    return false;
  }

  positiveAction() {
    if ( this.autoHideOnPositiveAction ) {
      this.showModal = false;
    }
    this.closeEmitter.next({
      action: ModalAction.POSITIVE
    });

    this.cdRef.detectChanges();

    return false;
  }

  cancelAction() {
    this.showModal = false;
    this.closeEmitter.next({
      action: ModalAction.CANCEL
    });

    this.cdRef.detectChanges();

    return false;
  }
}

/**
 * The possible reasons a modal has been closed.
 */
export enum ModalAction { POSITIVE, POSITIVE_EXTRA, CANCEL }
/**
 * Models the result of closing a modal dialog.
 */
export interface ModalResult {
  action: ModalAction;
}
